import { getUrlForHistoryState } from "client/Features/Documents/DocumentPage";
import { SharePointSideBarMenu } from "client/Features/Documents/SharePoint/SharePointSideBarMenu";
import {
	SharePointHistoryState,
	ViewsLoadingState,
	applySiteContent,
	createRootFolderFromView
} from "client/Features/Documents/SharePoint/SharePointStore";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import {
	DocumentsViewsStructureModel,
	getDocumentsViewStructure,
	LoadedSiteContentViewModel
} from "client/Api/Documents/SharePoint/sharepointDocuments";
import { DocumentsViewModel } from "client/Api/Documents/SharePoint/common";
import { RootFolder } from "client_legacy/Documents/SharePoint/models";
import { DocumentStoreType } from "client/Api/Documents/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { DestinationWindow } from "client/Api/Apps/common";

export type SharePointSectionProps = {
	hasInitializationError: boolean;
};

export const SharePointSection = (props: SharePointSectionProps): ReactElement => {
	const {
		hasInitializationError
	} = props;

	const [viewsLoadingState, setViewsLoadingState] = useState<ViewsLoadingState>({ kind: "loading" });

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const loadViews = useCallback(
		async (): Promise<void> => {
			try {
				setViewsLoadingState({ kind: "loading" });
				const result: DocumentsViewsStructureModel = await getDocumentsViewStructure(false, globalCancellation);

				setViewsLoadingState({
					kind: "success",
					items: result.structure
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setViewsLoadingState({ kind: "error" });
			}
		},
		[
			alertManager,
			globalCancellation
		]
	);

	useEffect(
		(): void => {
			if (hasInitializationError) {
				setViewsLoadingState({ kind: "error" });
			} else {
				loadViews();
			}
		},
		[
			alertManager,
			hasInitializationError,
			loadViews
		]
	);

	const selectViewHandler = useCallback(
		(view: DocumentsViewModel): void => {
			const rootFolder: RootFolder = createRootFolderFromView(view);

			const state: SharePointHistoryState = {
				store: DocumentStoreType.SharePoint,
				view,
				folder: rootFolder
			};

			const url: string = getUrlForHistoryState(state);

			const { destinationWindow } = userConfiguration().document;

			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[]
	);

	const siteContentLoadedHandler = useCallback(
		(id: string, content: LoadedSiteContentViewModel): void => {
			setViewsLoadingState(
				(x) => applySiteContent(
					x,
					id,
					content
				)
			);
		},
		[]
	);

	return (
		<SharePointSideBarMenu
			selectedView={undefined}
			viewsLoadingState={viewsLoadingState}
			onSelectView={selectViewHandler}
			doNotRenderActions
			onSiteContentLoaded={siteContentLoadedHandler}
		/>
	);
};
SharePointSection.displayName = "SharePointSection";